import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useWizardStore } from '../stores/wizardStore';
import {  Sparkles, Building2, BarChart3, Users, CheckCircle2, FileText, ArrowRight, X } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { api } from '../lib/api';
import {
  readDraft,
  writeDraft,
  clearDraft,
  formatRelativeTime,
  WizardDraft,
} from '../lib/analysisWizardDraft';
import Step1CompanySelection from './wizard/Step1CompanySelection';
import Step2IndustryResearch from './wizard/Step2IndustryResearch';
import Step3CompetitionAnalysis from './wizard/Step3CompetitionAnalysis';
import Step4StrategicQuestions from './wizard/Step4StrategicQuestions';
import Step5ReviewSubmit from './wizard/Step5ReviewSubmit';

// Step component type
interface StepConfig {
  number: number;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const steps: StepConfig[] = [
  {
    number: 1,
    title: 'Company Selection',
    icon: <Building2 className="w-5 h-5" />,
    description: 'Search or enter company details',
  },
  {
    number: 2,
    title: 'Industry Research',
    icon: <BarChart3 className="w-5 h-5" />,
    description: 'Amplinth-powered market analysis',
  },
  {
    number: 3,
    title: 'Competition Analysis',
    icon: <Users className="w-5 h-5" />,
    description: 'Identify top competitors',
  },
  {
    number: 4,
    title: 'Strategic Questions',
    icon: <FileText className="w-5 h-5" />,
    description: 'Review generated questions',
  },
  {
    number: 5,
    title: 'Review & Submit',
    icon: <CheckCircle2 className="w-5 h-5" />,
    description: 'Finalize and create case',
  },
];

export const NewCaseWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const { currentStep, setCurrentStep, nextStep, previousStep, resetWizard, loadWizardData } =
    useWizardStore();

  // Per-case draft pointer (parallel to AnalysisWizard).  Without this, the
  // Zustand `currentStep` is global so opening a previously-drafted case
  // lands on whatever step a *different* case left it at (or 1 if the store
  // was cleared) instead of the step the user actually saved on this case.
  const initialDraftRef = useRef<WizardDraft | null>(readDraft(caseId, 'discovery'));
  const [showResumeBanner, setShowResumeBanner] = useState<boolean>(
    initialDraftRef.current !== null && initialDraftRef.current.step > 1,
  );

  // Fetch case data if caseId is provided (edit mode)
  const { data: caseData, isLoading: loadingCase } = useQuery({
    queryKey: ['case', caseId],
    queryFn: async () => {
      if (!caseId) return null;
      const response = await api.get(`/cases/${caseId}`);
      return response;
    },
    enabled: !!caseId,
  });

  // Load wizard data for edit-mode OR reset for new-case mode.
  // Without this reset, the persisted `case-wizard-state` from a prior
  // session (Zustand persist middleware) leaks into a fresh "New Case" run
  // and the user sees stale company/industry/competitor/questions data.
  useEffect(() => {
    if (caseId) {
      // Edit mode — wait for the case fetch, then hydrate the data fields.
      // The draft step pointer is restored in a separate effect below so it
      // fires even when the case has no wizardData persisted yet (common
      // for cases saved at Step 1 before any server-side persistence).
      if (caseData && caseData.wizardData) {
        console.log('✅ Loading wizard data for edit mode:', caseData.wizardData);
        loadWizardData(caseData.wizardData);
      }
    } else {
      // New-case mode — wipe any persisted state from a previous session.
      console.log('🆕 New case — resetting wizard state');
      resetWizard();
    }
  }, [caseId, caseData, loadWizardData, resetWizard]);

  // Restore the per-case resume step.  Two-source resolution because
  // Discovery is fundamentally different from Analysis:
  //
  //   In Analysis: the case already exists when the wizard loads, so we
  //   write a per-step draft pointer to localStorage and read it back.
  //
  //   In Discovery: there is NO caseId until Step 5 submits the case
  //   (POST /cases happens then), so writeDraft is a no-op for the entire
  //   create flow.  After submit, `resetWizard()` runs and the user
  //   navigates away — no draft for the newly-created case is ever
  //   written.  Result: re-opening lands at Step 1 with prefilled data.
  //
  // Resolution order:
  //   1. Explicit draft pointer (most accurate, but rarely written for
  //      Discovery — only set if user re-edits an already-created case
  //      and advances a step).
  //   2. Infer the highest meaningful step from the loaded `wizardData`
  //      fields — companyData → Step 2, industryData → Step 3,
  //      competitorsData → Step 4, questionsData → Step 5.  This is the
  //      common case: an already-submitted case re-opened from Case
  //      Library lands at Step 5 (or wherever its data reaches).
  //
  // Fires once per caseId; guarded by `draftAppliedRef`.
  const draftAppliedRef = useRef<string | null>(null);
  const [resumedFromInference, setResumedFromInference] = useState(false);
  useEffect(() => {
    if (!caseId) return;
    if (draftAppliedRef.current === caseId) return;

    // (1) Explicit draft pointer
    const draft = initialDraftRef.current;
    if (draft && draft.step >= 1 && draft.step <= steps.length) {
      console.log('📍 Resuming Discovery wizard at saved draft step:', draft.step);
      setCurrentStep(draft.step as 1 | 2 | 3 | 4 | 5);
      draftAppliedRef.current = caseId;
      return;
    }

    // (2) Inferred step from wizardData — needs caseData to have loaded.
    // We don't mark the ref as applied until caseData is in, so this
    // effect can re-run when caseData arrives.
    if (caseData?.wizardData) {
      const wd = caseData.wizardData;
      let inferredStep: 1 | 2 | 3 | 4 | 5 = 1;
      if (wd.companyData) inferredStep = 2;
      if (wd.industryData) inferredStep = 3;
      if (Array.isArray(wd.competitorsData) && wd.competitorsData.length > 0) inferredStep = 4;
      if (Array.isArray(wd.questionsData) && wd.questionsData.length > 0) inferredStep = 5;
      if (inferredStep > 1) {
        console.log('📍 Inferring Discovery resume step from wizardData:', inferredStep);
        setCurrentStep(inferredStep);
        setResumedFromInference(true);
        setShowResumeBanner(true);
      }
      draftAppliedRef.current = caseId;
    }
  }, [caseId, caseData, setCurrentStep]);

  // Persist the current step every time it changes so refreshes and
  // tab-closes resume cleanly on next case-open.  Skip Step 1 — that's
  // the same as "no draft", and we don't want accidental tab visits to
  // overwrite a more advanced saved step.
  useEffect(() => {
    if (!caseId) return;
    if (currentStep > 1) {
      writeDraft(caseId, currentStep, 'discovery');
    }
  }, [caseId, currentStep]);

  const handleStepComplete = () => {
    nextStep();
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/');
    } else {
      previousStep();
    }
  };

  const currentStepConfig = steps[currentStep - 1];

  // Show loading spinner while fetching case data for edit mode
  if (caseId && loadingCase) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-neutral-600">Loading case data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-8 h-8 text-primary-600" />
            <h1 className="text-4xl font-bold text-neutral-900">
              Amplinth IntelliKnow<sup className="text-2xl">™</sup>
            </h1>
          </div>
          <p className="text-lg text-neutral-600">
            Create data-driven cases in hours with Amplinth assistance
          </p>
        </div>

        {/* Resume banner — shown when either a saved draft was restored
            OR a resume step was inferred from existing wizardData.  Tells
            the user we picked up where they left off and lets them jump
            back to Step 1 without losing the case data. */}
        {showResumeBanner && (initialDraftRef.current || resumedFromInference) && (
          <div className="mb-6 flex items-center justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Resuming where you left off
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {initialDraftRef.current ? (
                    <>
                      Draft saved {formatRelativeTime(initialDraftRef.current.savedAt)} at{' '}
                      <span className="font-medium">
                        {steps[initialDraftRef.current.step - 1]?.title ||
                          `Step ${initialDraftRef.current.step}`}
                      </span>
                      .
                    </>
                  ) : (
                    <>
                      Picking up at{' '}
                      <span className="font-medium">
                        {steps[currentStep - 1]?.title || `Step ${currentStep}`}
                      </span>{' '}
                      based on the data already in this case.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentStep(1);
                  clearDraft(caseId, 'discovery');
                  setShowResumeBanner(false);
                }}
              >
                Start from beginning
              </Button>
              <button
                onClick={() => setShowResumeBanner(false)}
                className="p-1 text-amber-700 hover:text-amber-900 rounded transition-colors"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <Card className="mb-8 p-6 bg-white shadow-md">
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-1 bg-neutral-200">
              <div
                className="h-full bg-primary-500 transition-all duration-500"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>

            {/* Steps */}
            <div className="relative flex justify-between">
              {steps.map((step) => {
                const isCompleted = currentStep > step.number;
                const isCurrent = currentStep === step.number;
                const isUpcoming = currentStep < step.number;

                return (
                  <div
                    key={step.number}
                    className="flex flex-col items-center"
                    style={{ width: `${100 / steps.length}%` }}
                  >
                    {/* Step Circle */}
                    <div
                      className={`
                        relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all
                        ${isCompleted ? 'bg-primary-500 border-primary-500 text-white' : ''}
                        ${isCurrent ? 'bg-white border-primary-500 text-primary-600 shadow-lg ring-4 ring-primary-100' : ''}
                        ${isUpcoming ? 'bg-white border-neutral-300 text-neutral-400' : ''}
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        step.icon
                      )}
                    </div>

                    {/* Step Label */}
                    <div className="mt-3 text-center">
                      <p
                        className={`text-sm font-semibold ${
                          isCurrent ? 'text-primary-600' : isCompleted ? 'text-neutral-700' : 'text-neutral-400'
                        }`}
                      >
                        {step.title}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isCurrent ? 'text-neutral-600' : 'text-neutral-400'
                        }`}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Current Step Content */}
        <div className="mb-8">
          <Card className="p-8 bg-white shadow-md">
            {currentStep === 1 && <Step1CompanySelection onNext={handleStepComplete} />}
            {currentStep === 2 && (
              <Step2IndustryResearch
                onNext={handleStepComplete}
                onBack={handleBack}
              />
            )}
            {currentStep === 3 && (
              <Step3CompetitionAnalysis
                onNext={handleStepComplete}
                onBack={handleBack}
              />
            )}
            {currentStep === 4 && (
              <Step4StrategicQuestions
                onNext={handleStepComplete}
                onBack={handleBack}
              />
            )}
            {currentStep === 5 && (
              <Step5ReviewSubmit
                onNext={handleStepComplete}
                onBack={handleBack}
                caseId={caseId || undefined}
              />
            )}
          </Card>
        </div>

        {/* Help Text */}
        <div className="text-center text-sm text-neutral-500">
          <p>
            Need help? This wizard uses Amplinth IntelliKnow<sup className="text-xs">™</sup> to automate 60-80% of discovery work.
            You can review and edit generated content at each step.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewCaseWizard;
